import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Typography } from '@mui/material';
import SearchInputCDS from '@sharedCR/coreDesignSystem/SearchInputCDS';
import { CONTACT_SEARCH_DEBOUNCE_MS } from 'app/pages/crm/shared/crm.constants';
import { crmContactApi } from 'app/services/api';
import PropTypes from 'prop-types';

import useDebounce from 'hooks/useDebounce';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { showErrorMessageAction } from 'store/global/actions';

import { CONTACT_SEARCH_MIN_CHARS } from '../../CrmContactSection.constants';
import {
  getContactIdValue,
  getSuggestedContacts,
  getSuggestionQueries,
  normalizeContactsResponse,
} from '../../CrmContactSection.utils';

import ContactResultRow from './ContactResultRow';
import { emptyStyles, resultsStyles, wrapperStyles } from './FindContactPanel.styles';

const FindContactPanel = ({ borrower, onContactChosen }) => {
  const dispatch = useDispatch();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  const searchContacts = useDebounce(async searchText => {
    try {
      const { data } = await crmContactApi.getContacts({ search: searchText });

      setResults(normalizeContactsResponse(data));
    } catch (err) {
      dispatch(showErrorMessageAction({ err }));
    }
  }, CONTACT_SEARCH_DEBOUNCE_MS);

  const onSearch = useCallback(
    value => {
      const trimmed = value.trim();

      setQuery(trimmed);

      if (trimmed.length < CONTACT_SEARCH_MIN_CHARS) {
        setResults([]);

        return;
      }

      searchContacts(trimmed);
    },
    [searchContacts],
  );

  const isSearching = query.length >= CONTACT_SEARCH_MIN_CHARS;
  const visibleContacts = isSearching ? results : suggestions;

  const onPick = useCallback(
    contact =>
      onContactChosen({
        contactId: getContactIdValue(contact),
        firstName: contact.firstName,
        lastName: contact.lastName,
      }),
    [onContactChosen],
  );

  const renderedRows = useMemo(
    () =>
      visibleContacts.map(contact => (
        <ContactResultRow contact={contact} key={getContactIdValue(contact)} onPick={onPick} />
      )),
    [visibleContacts, onPick],
  );

  // Borrower-matched contacts shown as the initial list before the user types.
  useEffectSafeStrictMode(() => {
    const lifecycle = { cancelled: false };

    const loadSuggestions = async () => {
      const queries = getSuggestionQueries(borrower);

      if (queries.length === 0) {
        return;
      }

      try {
        const responses = await Promise.all(
          queries.map(search =>
            crmContactApi
              .getContacts({ search })
              .then(({ data }) => normalizeContactsResponse(data))
              .catch(() => []),
          ),
        );

        if (!lifecycle.cancelled) {
          setSuggestions(getSuggestedContacts(borrower, responses.flat()));
        }
      } catch (err) {
        if (!lifecycle.cancelled) {
          dispatch(showErrorMessageAction({ err }));
        }
      }
    };

    loadSuggestions();

    return () => {
      lifecycle.cancelled = true;
    };
  }, [borrower, dispatch]);

  return (
    <Box sx={wrapperStyles}>
      <SearchInputCDS
        searchOnType
        ariaLabel="Search contacts by name or email"
        placeholder="Search by name or email…"
        validQueryLength={CONTACT_SEARCH_MIN_CHARS}
        onSearch={onSearch}
      />

      <Box sx={resultsStyles}>
        {visibleContacts.length === 0 && isSearching ? (
          <Typography sx={emptyStyles} variant="regular14">
            No contacts found — try Create new.
          </Typography>
        ) : (
          renderedRows
        )}
      </Box>
    </Box>
  );
};

FindContactPanel.propTypes = {
  borrower: PropTypes.shape({ id: PropTypes.string }).isRequired,
  onContactChosen: PropTypes.func.isRequired,
};

export default memo(FindContactPanel);
